import React, { useState } from "react";
import { Disclosure } from "@headlessui/react";
import { FiFilter } from "react-icons/fi";

const AuditFrameworks = () => {
  const frameworks = [
    {
      name: "COSO ERM Internal Control Framework",
      description: "Compliance Risk Management Applying the COSO ERM Framework. Published by COSO in 2020",
      documentUrl: "/wpauditframework/Compliance-Risk-Management-Applying-the-COSO-ERM-Framework.pdf",
      imageUrl: "/images/auditframework/cosoerm.png",
      source: "COSO",
      copyright: "*Copyright owned by COSO.",
      weblink: "Source: https://www.coso.org/", 
      topics: ["Internal Controls", "Governance"],
      industry: ["Finance", "Corporate"],
    },
    {
      name: "Global Internal Audit Standards",
      description: "The Standards guide the worldwide professional practice of internal auditing.",
      documentUrl: "/wpauditframework/globalinternalauditstandards_2024january9.pdf",
      imageUrl: "/images/auditframework/gias.png",
      source: "IIA",
      copyright: "*Copyright owned by IIA.",
      weblink: "Source: https://www.theiia.org/", 
      topics: ["Internal Audit", "Governance"],
      industry: ["Audit", "Corporate"],
    },
    {
      name: "A risk practitioner's guide to the ISO 31000:2018",
      description: "Review of the 2018 version of the ISO 31000 risk management guidelines.",
      documentUrl: "/wpauditframework/A Risk Practitioners Guide to ISO 31000 – 2018.pdf",
      imageUrl: "/images/auditframework/irmiso1.png",
      source: "IRM  ",
      copyright: "*Copyright owned by IRM.",
      weblink: "Source: https://www.theirm.org/", 
      topics: ["ERM", "Risk Management"],
      industry: ["Audit", "Corporate"],
    },


    
  ];

  const [selectedSource, setSelectedSource] = useState("");
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState("");

  const uniqueSources = [...new Set(frameworks.map((f) => f.source))];
  const uniqueTopics = [...new Set(frameworks.flatMap((f) => f.topics))];
  const uniqueIndustries = [...new Set(frameworks.flatMap((f) => f.industry))];

  const handleTopicChange = (topic) => {
    setSelectedTopics((prev) =>
      prev.includes(topic) ? prev.filter((t) => t !== topic) : [...prev, topic]
    );
  };

  const filteredFrameworks = frameworks.filter((framework) => {
    const matchesSource = !selectedSource || framework.source === selectedSource;
    const matchesTopics =
      selectedTopics.length === 0 || selectedTopics.some((topic) => framework.topics.includes(topic));
    const matchesIndustry = !selectedIndustry || framework.industry.includes(selectedIndustry);

    return matchesSource && matchesTopics && matchesIndustry;
  });

  return (
    <div className="flex flex-col lg:flex-row">
      {/* Filter Panel */}
      <aside className="lg:w-64 bg-gray-100 p-4 rounded-lg shadow-md hidden lg:block">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Filters</h3>
        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Source</h4>
          <select
            className="w-full mt-2 p-2 border rounded-md"
            value={selectedSource}
            onChange={(e) => setSelectedSource(e.target.value)}
          >
            <option value="">All Sources</option>
            {uniqueSources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Topics</h4>
          {uniqueTopics.map((topic) => (
            <div key={topic} className="flex items-center mt-2">
              <input
                type="checkbox"
                id={topic}
                value={topic}
                checked={selectedTopics.includes(topic)}
                onChange={() => handleTopicChange(topic)}
                className="mr-2"
              />
              <label htmlFor={topic} className="text-gray-600">
                {topic}
              </label>
            </div>
          ))}
        </div>

        <div className="mb-6">
          <h4 className="font-medium text-gray-600">Industry</h4>
          <select
            className="w-full mt-2 p-2 border rounded-md"
            value={selectedIndustry}
            onChange={(e) => setSelectedIndustry(e.target.value)}
          >
            <option value="">All Industries</option>
            {uniqueIndustries.map((industry) => (
              <option key={industry} value={industry}>
                {industry}
              </option>
            ))}
          </select>
        </div>
      </aside>

      {/* Mobile Filter Toggle (Headless UI) */}
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button className="lg:hidden flex items-center bg-blue-500 text-white px-4 py-2 rounded-md shadow-md w-full mb-4">
              <FiFilter className="mr-2" />
              {open ? "Hide Filters" : "Show Filters"}
            </Disclosure.Button>
            <Disclosure.Panel className="p-4 bg-gray-100 rounded-lg shadow-md lg:hidden">
              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Source</h4>
                <select
                  className="w-full mt-2 p-2 border rounded-md"
                  value={selectedSource}
                  onChange={(e) => setSelectedSource(e.target.value)}
                >
                  <option value="">All Sources</option>
                  {uniqueSources.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Topics</h4>
                {uniqueTopics.map((topic) => (
                  <div key={topic} className="flex items-center mt-2">
                    <input
                      type="checkbox"
                      id={topic}
                      value={topic}
                      checked={selectedTopics.includes(topic)}
                      onChange={() => handleTopicChange(topic)}
                      className="mr-2"
                    />
                    <label htmlFor={topic} className="text-gray-600">
                      {topic}
                    </label>
                  </div>
                ))}
              </div>

              <div className="mb-6">
                <h4 className="font-medium text-gray-600">Industry</h4>
                <select
                  className="w-full mt-2 p-2 border rounded-md"
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                >
                  <option value="">All Industries</option>
                  {uniqueIndustries.map((industry) => (
                    <option key={industry} value={industry}>
                      {industry}
                    </option>
                  ))}
                </select>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      {/* Framework Cards */}
      <div className="flex-1 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-6">Internal Audit Frameworks</h2>
        <p className="text-gray-600">
          Explore a curated list of audit frameworks that enhance internal audit effectiveness.
        </p>

        {/* Grid for All Screens */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-6">
          {filteredFrameworks.map((framework, index) => (
            <div
              key={index}
              className="bg-gray-200 rounded-lg p-4 flex flex-col items-center justify-center shadow-md"
            >
              <img
                src={framework.imageUrl}
                alt={`${framework.name} Image`}
                className="h-40 w-full rounded-md mb-4 object-contain"
              />
              <h3 className="text-lg font-semibold text-gray-700">
                {framework.name}
              </h3>
              <p className="text-gray-600 text-sm text-center mb-4">
                {framework.description}
              </p>
              <p className="text-gray-600 text-sm text-center mb-4">
                {framework.copyright}
              </p>
              <p className="text-gray-600 text-sm text-center mb-4">
                {framework.weblink}
              </p>
              <a
                href={framework.documentUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-blue-500 hover:underline"
              >
                View Document
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AuditFrameworks;
